import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode, type RefObject } from 'react';
import { withTimeout } from '../walks/pdfPages';
import { bodyCanvas, locateAll, locateQuote, quoteOnCanvas, type Canvas, type Part, type TextItem } from './pdfText';
import type { PDFDocumentProxy } from 'pdfjs-dist';

/*
 * Документ договора единым полотном с подсветкой замечаний (3.55, 3.57).
 *
 * PDF делает сервер из docx, а показываем его здесь тем же приёмом, что и
 * альбом планировок (walks/pdfPages.ts): pdfjs подключается динамическим
 * импортом внутри эффекта — библиотека весит мегабайты и не должна лежать в
 * общем бандле ради одного экрана.
 *
 * Полотно, а не стопка листов (задача Ильи 21.09.2026). Замечание держится
 * цитатой, а абзац договора запросто начинается на одной странице и кончается
 * на другой: пока листы были отдельными, такой абзац нельзя было выделить
 * целиком и нельзя было завести по нему одно замечание. Теперь страницы идут
 * встык внутри одной бумаги, выделение мышью свободно переходит с одной на
 * другую, а цитата ищется по всему полотну (`locateAll`).
 *
 * Поверх страницы два слоя. Нижний — прозрачный текстовый `.doc-layer`: те же
 * куски текста в тех же местах, чтобы выделение мышью работало само,
 * браузерное, без своей обработки перетаскивания. Верхний — прямоугольники:
 * замечания `.doc-hl` и отложенное выделение `.doc-sel`. Их места считает
 * `locateAll` по слепку, а не хранит сервер, поэтому подсветка переживает и
 * новую вёрстку PDF, и смену масштаба.
 *
 * Своя подсветка выделения нужна потому, что родное выделение браузера
 * пропадает от первого же нажатия на кнопку «Комментировать»: пока открыта
 * форма замечания, человек должен видеть, к какому месту он пишет.
 *
 * Поле с пузырями (3.56) рисует не этот компонент, а тот, кто его позвал:
 * `children(anchors)` встаёт вторым столбцом сетки `.doc-canvas`. Поле одно на
 * весь договор, и места якорей приходят в пикселях от верха полотна: считает
 * их `locateAll` по слепку, и другого способа узнать, где стоит выделение, у
 * поля нет.
 */

/** Сколько ждать один шаг разбора, прежде чем считать это зависанием: иначе
 *  сбой воркера выглядит как вечное «Загружаю документ…». */
const STEP_TIMEOUT_MS = 20_000;

/** Потолок растра по ширине: дальше канва только дорожает — текст от этого
 *  резче не становится, а память кончается. */
const MAX_RASTER = 2400;

/** Пол растра: на телефоне полотно узкое, но читаться должно и там. */
const MIN_RASTER = 800;

/** Шаг растра. Без него каждый пиксель ширины окна перерисовывал бы документ:
 *  тянуть край окна мышью значило бы гонять pdfjs без остановки. */
const RASTER_STEP = 200;

/** Запас отрисовки вокруг окна прокрутки: полтора экрана вверх и вниз. */
const NEAR_MARGIN = '150% 0px';

/** Зазор от низа выделения до всплывашки «Комментировать». */
const POP_GAP = 6;

export type DocHighlight = {
  id: string;
  quote: string;
  state: 'on' | 'done' | 'moved' | '';
};

/** Место ветки на поле: страница нужна адресу «С. 3», отступ — самому полю. */
export type DocAnchor = { id: string; page: number; top: number };

type Props = {
  pdfUrl: string;
  highlights: DocHighlight[];
  selectedId: string | null;
  onSelectHighlight: (id: string) => void;
  /** `top` — пиксели от верха полотна: новому замечанию нужно своё место на поле. */
  onSelectText: (sel: { quote: string; top: number }) => void;
  canComment: boolean;
  /** Во сколько раз полотно крупнее ширины зоны; 1 — по ширине. */
  zoom: number;
  /** Узкий экран: вместо поля на выделении счётчик `.doc-mark` по порядку. */
  markers?: boolean;
  /** Подсказка во всплывашке: номер пункта. Слепок версии держит тот, кто
   *  позвал компонент, — здесь есть только цитата. */
  clause?: (quote: string) => string;
  /**
   * Общая область прокрутки ленты. Документов в версии несколько, а
   * прокручиваемая область на экране ровно одна (правило телефона), поэтому
   * заводит её тот, кто выводит ленту, и передаёт сюда. Пусто — компонент
   * прокручивается сам, как и раньше.
   */
  scroll?: RefObject<HTMLDivElement | null>;
  children?: (anchors: DocAnchor[]) => ReactNode;
};

/** Страница, как её отдал pdfjs: размер в точках и весь текст в долях страницы. */
type Raw = { w: number; h: number; items: TextItem[] };

/**
 * Страница полотна. `items` — тело без колонтитулов, его и видит человек в
 * слое текста; `printed` — всё, что на странице напечатано, и нужно оно только
 * затем, чтобы найти место замечанию, написанному до перехода на полотно
 * (`locateQuote`).
 */
type Page = Raw & { printed: TextItem[] };

type Pop = { quote: string; hint: string; left: number; top: number; anchor: number };

/** Отложенное выделение: живёт, пока родитель не завёл по нему ветку. */
type Pending = { quote: string; parts: Part[] };

/** Пробелы в один: цитата нормализована, а замечание пришло с сервера. */
const flat = (text: string): string => text.replace(/\s+/g, ' ').trim();

/** Кусок текста pdfjs в доли страницы. `viewport.transform` переводит систему
 *  PDF (y снизу) в экранную (y сверху), дальше делим на размер страницы. */
function toItem(
  item: { str: string; width: number; hasEOL?: boolean; transform: number[] },
  transform: number[],
  vw: number,
  vh: number,
  mul: (a: number[], b: number[]) => number[],
): TextItem {
  const t = mul(transform, item.transform);
  const h = Math.hypot(t[2], t[3]);
  return {
    str: item.hasEOL ? `${item.str}\n` : item.str,
    x: t[4] / vw,
    y: (t[5] - h) / vh,
    w: item.width / vw,
    h: h / vh,
  };
}

export function DocPages({
  pdfUrl,
  highlights,
  selectedId,
  onSelectHighlight,
  onSelectText,
  canComment,
  zoom,
  markers,
  clause,
  scroll,
  children,
}: Props) {
  const [pages, setPages] = useState<Page[]>([]);
  const [failed, setFailed] = useState(false);
  const [pop, setPop] = useState<Pop | null>(null);
  const [pending, setPending] = useState<Pending | null>(null);
  const docRef = useRef<PDFDocumentProxy | null>(null);
  const canvases = useRef<(HTMLCanvasElement | null)[]>([]);
  const boxes = useRef<(HTMLDivElement | null)[]>([]);
  const ownScroll = useRef<HTMLDivElement | null>(null);
  const scrollRef = scroll ?? ownScroll;
  /** Своей прокрутки нет — значит документ стоит в общей ленте. */
  const inFeed = !!scroll;
  const flowRef = useRef<HTMLDivElement | null>(null);
  const [paperW, setPaperW] = useState(0);
  /** Страницы у окна прокрутки: только они и нарисованы. */
  const [near, setNear] = useState<number[]>([]);
  /** Ширина растра, которым страница нарисована сейчас. */
  const drawn = useRef(new Map<number, number>());

  // Открытие документа: сам файл, слепок текста и размеры страниц. Рисование
  // канвы — отдельным эффектом, и только тех страниц, что у окна.
  useEffect(() => {
    let alive = true;
    let worker: { terminate(): void } | null = null;
    let task: { promise: Promise<PDFDocumentProxy>; destroy(): Promise<void> } | null = null;

    setPages([]);
    setFailed(false);
    setPop(null);
    setPending(null);
    setNear([]);
    drawn.current.clear();

    (async () => {
      const res = await fetch(pdfUrl, { credentials: 'same-origin' });
      if (!res.ok) throw new Error('предпросмотр недоступен');
      const data = await res.arrayBuffer();

      const pdfjs = await import('pdfjs-dist');
      const { default: PdfWorker } = await import('pdfjs-dist/build/pdf.worker.min.mjs?worker');
      if (!alive) return;
      worker = new PdfWorker();
      pdfjs.GlobalWorkerOptions.workerPort = worker as Worker;

      task = pdfjs.getDocument({ data });
      const doc = await withTimeout(task.promise, STEP_TIMEOUT_MS, 'PDF не открылся: истекло время ожидания');
      if (!alive) return;

      const out: Raw[] = [];
      for (let n = 1; n <= doc.numPages; n++) {
        const page = await doc.getPage(n);
        const base = page.getViewport({ scale: 1 });
        const content = await page.getTextContent();
        out.push({
          w: base.width,
          h: base.height,
          items: content.items
            .filter((it): it is Extract<typeof it, { str: string }> => 'str' in it)
            .map((it) => toItem(it, base.transform, base.width, base.height, pdfjs.Util.transform)),
        });
      }
      if (!alive) return;
      // Колонтитулы из слоя текста вон: замечания к ним не бывает, а в цитате
      // через разрыв страницы номер страницы оказывался посередине.
      const body = bodyCanvas(out.map((p) => p.items));
      docRef.current = doc;
      setPages(out.map((p, i) => ({ ...p, items: body[i], printed: p.items })));
    })().catch(() => {
      if (alive) setFailed(true);
    });

    return () => {
      alive = false;
      docRef.current = null;
      task?.destroy().catch(() => {});
      worker?.terminate();
    };
  }, [pdfUrl]);

  // Ширина полотна нужна и текстовому слою (кегль задаётся в точках), и
  // растру. Меряем, а не считаем: ширину назначает таблица стилей, а масштаб —
  // доля от неё.
  useEffect(() => {
    const el = flowRef.current;
    if (!el) return;
    const measure = () => setPaperW(el.clientWidth);
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [pages.length]);

  // Какие страницы у окна прокрутки. Наблюдателя нет (jsdom) — рисуем все:
  // в тестах документа немного, а без списка не нарисовалось бы ничего.
  useEffect(() => {
    if (!pages.length) return;
    if (typeof IntersectionObserver === 'undefined') {
      setNear(pages.map((_, i) => i));
      return;
    }
    const shown = new Set<number>();
    const io = new IntersectionObserver(
      (list) => {
        let moved = false;
        for (const e of list) {
          const i = Number((e.target as HTMLElement).dataset.page);
          if (e.isIntersecting) {
            if (!shown.has(i)) {
              shown.add(i);
              moved = true;
            }
          } else if (shown.delete(i)) {
            moved = true;
          }
        }
        if (moved) setNear([...shown].sort((a, b) => a - b));
      },
      { root: scrollRef.current, rootMargin: NEAR_MARGIN },
    );
    for (const el of boxes.current) if (el) io.observe(el);
    return () => io.disconnect();
  }, [pages, scrollRef]);

  /*
   * Растр следует за шириной полотна и плотностью экрана: при увеличении
   * документ должен становиться крупнее, а не мыльнее. Ступень в 200 пикселей
   * — чтобы перетаскивание края окна не гоняло pdfjs без остановки.
   */
  const rasterW = useMemo(() => {
    if (!paperW) return 0;
    const dpr = Math.min(typeof devicePixelRatio === 'number' ? devicePixelRatio : 1, 2);
    const want = Math.ceil((paperW * dpr) / RASTER_STEP) * RASTER_STEP;
    return Math.max(MIN_RASTER, Math.min(MAX_RASTER, want));
  }, [paperW]);

  // Ушедшие страницы освобождают память: канва 2400 по ширине — это десятки
  // мегабайт, и полсотни таких страниц браузер не держит. Коробка страницы
  // остаётся на месте — её высоту задаёт `aspect-ratio`, а не канва.
  useEffect(() => {
    const keep = new Set(near);
    for (const i of [...drawn.current.keys()]) {
      if (keep.has(i)) continue;
      const canvas = canvases.current[i];
      if (canvas) {
        canvas.width = 1;
        canvas.height = 1;
      }
      drawn.current.delete(i);
    }
  }, [near]);

  // Рисование страниц у окна.
  useEffect(() => {
    const doc = docRef.current;
    if (!doc || !pages.length || !rasterW) return;
    let alive = true;
    let task: { promise: Promise<void>; cancel(): void } | null = null;

    (async () => {
      for (const i of near) {
        if (!alive) return;
        const canvas = canvases.current[i];
        if (!canvas || drawn.current.get(i) === rasterW) continue;
        const page = await doc.getPage(i + 1);
        if (!alive) return;
        const viewport = page.getViewport({ scale: rasterW / pages[i].w });
        canvas.width = Math.round(viewport.width);
        canvas.height = Math.round(viewport.height);
        task = page.render({ canvas, viewport });
        await withTimeout(task.promise, STEP_TIMEOUT_MS, `Страница ${i + 1}: истекло время ожидания`);
        task = null;
        if (!alive) return;
        drawn.current.set(i, rasterW);
      }
    })().catch(() => {});

    return () => {
      alive = false;
      task?.cancel();
    };
  }, [pages, near, rasterW]);

  /** Полотно для поиска цитат: слепки страниц по порядку. */
  const canvas = useMemo<Canvas>(() => pages.map((p) => p.items), [pages]);

  /** То же полотно, но со всем напечатанным: запасной заход для старых цитат. */
  const printed = useMemo<Canvas>(() => pages.map((p) => p.printed), [pages]);

  /** Где какая страница стоит в полотне и какой она высоты. */
  const geom = useMemo(() => {
    const tops: number[] = [];
    const hs: number[] = [];
    let top = 0;
    for (const p of pages) {
      const h = paperW ? (paperW * p.h) / p.w : 0;
      tops.push(top);
      hs.push(h);
      top += h;
    }
    return { tops, hs };
  }, [pages, paperW]);

  /** Отступ места в полотне от его верха, в пикселях. */
  const topOf = (part: Part) => geom.tops[part.page] + part.rect.y * geom.hs[part.page];

  /** Замечание ищется по всему полотну: абзац через разрыв страницы — обычное
   *  дело, и подсветка у него на двух страницах. */
  const marks = useMemo(() => {
    const out: { id: string; state: string; no: number; parts: Part[] }[] = [];
    for (const [no, hl] of highlights.entries()) {
      const parts = locateQuote(canvas, printed, hl.quote);
      if (parts.length) out.push({ id: hl.id, state: hl.state, no: no + 1, parts });
    }
    return out;
  }, [canvas, printed, highlights]);

  const anchors = useMemo(
    () =>
      marks
        .map((m) => ({ id: m.id, page: m.parts[0].page, top: topOf(m.parts[0]) }))
        .sort((a, b) => a.top - b.top),
    // topOf держится на geom, и другой зависимости у него нет
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [marks, geom],
  );

  /** Отложенное выделение рисуется, пока по нему не завели ветку: тогда то же
   *  место уже показывает `.doc-hl`, и две заливки наложились бы друг на друга. */
  const shown = useMemo(() => {
    if (!pending) return null;
    return highlights.some((h) => flat(h.quote) === flat(pending.quote)) ? null : pending;
  }, [pending, highlights]);

  /*
   * Конец выделения: мышью — `mouseup`, пальцем — `touchend`. Подрядчик,
   * единственный, кто приходит с телефона, выделяет длинным нажатием, и
   * `mouseup` там не приходит вовсе: без `touchend` замечание с телефона
   * начать было нельзя. Разбор отложен на шаг очереди: выделение после
   * длинного нажатия браузер дособирает уже после события.
   *
   * Обработчик висит на всём полотне, а не на странице: выделение, начатое на
   * одной странице и законченное на другой, — это ровно тот случай, ради
   * которого полотно и сделано.
   */
  const onSelectionEnd = (el: HTMLElement) => {
    const sel = window.getSelection();
    const text = sel?.toString() ?? '';
    const drop = () => {
      setPop(null);
      setPending(null);
    };
    if (!canComment || !sel || sel.isCollapsed || !text.trim()) {
      drop();
      return;
    }
    const quote = quoteOnCanvas(canvas, text);
    if (!quote) {
      drop();
      return;
    }
    const parts = locateAll(canvas, quote);
    const box = el.getBoundingClientRect();
    const at = sel.getRangeAt(0).getBoundingClientRect();
    setPending({ quote, parts });
    setPop({
      quote,
      hint: clause?.(quote) ?? '',
      left: at.left + at.width / 2 - box.left,
      top: at.bottom - box.top + POP_GAP,
      anchor: parts.length ? topOf(parts[0]) : Math.max(0, at.top - box.top),
    });
  };

  const wrap = (node: ReactNode) =>
    inFeed ? node : (
      <div className="doc-scroll" ref={ownScroll}>
        {node}
      </div>
    );

  if (failed) return wrap(<div className="empty">Предпросмотр документа недоступен</div>);
  if (!pages.length) return wrap(<div className="empty">Загружаю документ…</div>);

  return wrap(
    <>
      <div className="doc-canvas" style={{ '--doc-zoom': zoom } as CSSProperties}>
        <div
          className="doc-flow"
          ref={flowRef}
          onMouseUp={(e) => onSelectionEnd(e.currentTarget)}
          onTouchEnd={(e) => {
            const el = e.currentTarget;
            setTimeout(() => onSelectionEnd(el), 0);
          }}
        >
          {pages.map((page, index) => (
            <div
              className="doc-page"
              key={index}
              data-page={index}
              ref={(el) => {
                boxes.current[index] = el;
              }}
              style={{ aspectRatio: `${page.w} / ${page.h}` }}
            >
              {/* Размер растра ставится кодом рисования, а не разметкой: иначе
                  очередной вывод React стёр бы уже нарисованную страницу. */}
              <canvas ref={(el) => { canvases.current[index] = el; }} />

              {/* Прозрачный текстовый слой: выделение мышью — браузерное. */}
              <div className="doc-layer">
                {page.items.map((it, j) => (
                  <span
                    key={j}
                    style={{
                      left: `${it.x * 100}%`,
                      top: `${it.y * 100}%`,
                      fontSize: `${it.h * geom.hs[index]}px`,
                    }}
                  >
                    {it.str}
                  </span>
                ))}
              </div>

              {shown?.parts
                .filter((p) => p.page === index)
                .map((p, j) => (
                  <div
                    key={`sel-${j}`}
                    className="doc-sel"
                    style={{
                      left: `${p.rect.x * 100}%`,
                      top: `${p.rect.y * 100}%`,
                      width: `${p.rect.w * 100}%`,
                      height: `${p.rect.h * 100}%`,
                    }}
                  />
                ))}

              {marks.flatMap((m) =>
                m.parts
                  .filter((p) => p.page === index)
                  .map((p, j) => (
                    <div
                      key={`${m.id}-${j}`}
                      className={`doc-hl ${m.id === selectedId ? 'on' : m.state}`.trim()}
                      style={{
                        left: `${p.rect.x * 100}%`,
                        top: `${p.rect.y * 100}%`,
                        width: `${p.rect.w * 100}%`,
                        height: `${p.rect.h * 100}%`,
                      }}
                      onClick={() => onSelectHighlight(m.id)}
                    />
                  )),
              )}

              {/* Узкий экран: на выделении счётчик у правого края полотна — по
                  нему ветка открывается нижним листом (3.56, раскладка Б). */}
              {markers &&
                marks
                  .filter((m) => m.parts[0].page === index)
                  .map((m) => (
                    <div
                      key={`n-${m.id}`}
                      className={m.state === 'done' ? 'doc-mark done' : 'doc-mark'}
                      style={{
                        left: `${(m.parts[0].rect.x + m.parts[0].rect.w) * 100}%`,
                        top: `${m.parts[0].rect.y * 100}%`,
                      }}
                    >
                      <span className="counter sm" onClick={() => onSelectHighlight(m.id)}>
                        {m.no}
                      </span>
                    </div>
                  ))}
            </div>
          ))}

          {!!pop && (
            <div className="doc-pop" style={{ left: pop.left, top: pop.top }}>
              <button
                className="btn sm primary"
                type="button"
                onClick={() => {
                  onSelectText({ quote: pop.quote, top: pop.anchor });
                  setPop(null);
                }}
              >
                Комментировать
              </button>
              {!!pop.hint && <span className="hint">{pop.hint}</span>}
            </div>
          )}
        </div>

        {children?.(anchors)}
      </div>
    </>,
  );
}
